'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { motion, useInView, type Variants } from 'motion/react';
import { useQuality } from '@/components/shared/QualityProvider';
import { CHAPTER_REVEAL, EASE_OUT_QUART, HEADLINE_WIPE } from '@/lib/motionTokens';

// One InfoSection station. Each station is exactly one viewport tall and
// pins its content NEXT TO the TrailingBlob's parking spot for that chapter
// — every chapter gets a unique class modifier (`chapter-station--01`, …)
// whose CSS hand-tunes top/right/bottom/left to land beside the blob's
// world coordinates from TrailingBlob.STATION_POSITIONS. The headline
// reveals via a single clip-path mask wipe (no per-word stagger); lead
// and tail still drop in with the blur+y line variant.

export type ChapterStationProps = {
  chapter: string;
  headline: string;
  lead?: string;
  headlineClassName?: string;
  tail?: ReactNode;
  // Wipe direction follows the chapter's text alignment so the curtain
  // opens from the same edge the text reads from. Default is 'left'
  // (left-aligned text → wipe opens left→right).
  align?: 'left' | 'right' | 'center';
  // When true, the tail's opacity follows scroll position past the
  // station — fades as soon as the section starts moving up so it
  // doesn't overlap with a fixed overlay above (e.g. the W mount on
  // chapter 04). The motion variant still controls the entry reveal;
  // this just multiplies a scroll-driven factor on top.
  tailFadeOnScroll?: boolean;
};

// All reveal timings live in `lib/motionTokens.ts`. Each child carries its
// own explicit delay rather than reading from a shared staggerChildren —
// so the headline → lead → tail cascade can have non-uniform gaps and
// different eases per line (see CHAPTER_REVEAL comment).
const containerVariants: Variants = {
  hidden: {},
  visible: {},
};

// Lead — short rise, modest blur, brisk EASE_OUT_QUART arrival.
const leadVariants: Variants = {
  hidden: {
    opacity: 0,
    y: CHAPTER_REVEAL.leadOffsetPx,
    filter: `blur(${CHAPTER_REVEAL.leadBlurPx}px)`,
  },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: CHAPTER_REVEAL.durationSeconds,
      ease: EASE_OUT_QUART,
      delay: CHAPTER_REVEAL.leadDelaySeconds,
    },
  },
};

// Tail — longer travel, heavier blur, softer expo-out so the line
// settles in place instead of arriving on the same beat as the lead.
const tailVariants: Variants = {
  hidden: {
    opacity: 0,
    y: CHAPTER_REVEAL.tailOffsetPx,
    filter: `blur(${CHAPTER_REVEAL.tailBlurPx}px)`,
  },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: CHAPTER_REVEAL.durationSeconds,
      ease: CHAPTER_REVEAL.tailEase,
      delay: CHAPTER_REVEAL.tailDelaySeconds,
    },
  },
};

// Mask wipes — clip-path inset edges that retreat to zero. The hidden
// state is direction-specific (which edge starts closed); visible is
// always inset(0). HEADLINE_WIPE drives the duration + ease; the delay
// holds the wipe back until the station has been in view briefly.
const buildWipeTransition = () => ({
  duration: HEADLINE_WIPE.durationSeconds,
  ease: HEADLINE_WIPE.ease,
  delay: CHAPTER_REVEAL.headlineDelaySeconds,
});

// Vertical breathing room on every inset: the headlines run line-height
// 0.92, so first-line diacritics (Ã, Ê, Á) sit ABOVE the content box and
// a cedilla (Ç) on the last line dips below it. A wipe ending at inset(0)
// would crop them permanently — "NÃO" reads as "NAO". The negative em
// values extend the clip window past the box on both vertical edges while
// the horizontal edges still drive the wipe.
const WIPE_V = '-0.3em';

const headlineWipeVariants: Record<NonNullable<ChapterStationProps['align']>, Variants> = {
  left: {
    hidden: { clipPath: `inset(${WIPE_V} 100% ${WIPE_V} 0)` },
    visible: { clipPath: `inset(${WIPE_V} 0 ${WIPE_V} 0)`, transition: buildWipeTransition() },
  },
  right: {
    hidden: { clipPath: `inset(${WIPE_V} 0 ${WIPE_V} 100%)` },
    visible: { clipPath: `inset(${WIPE_V} 0 ${WIPE_V} 0)`, transition: buildWipeTransition() },
  },
  center: {
    hidden: { clipPath: `inset(${WIPE_V} 50% ${WIPE_V} 50%)` },
    visible: { clipPath: `inset(${WIPE_V} 0 ${WIPE_V} 0)`, transition: buildWipeTransition() },
  },
};

// How much of the station has to scroll past viewport top before the
// scroll-faded tail is fully invisible. 15vh = a snappy decay window
// that gets the tail out of the way before the user lands far enough
// into the next section to overlap a fixed overlay (e.g. the W mount).
const TAIL_SCROLL_FADE_VH = 15;

export function ChapterStation({
  chapter,
  headline,
  lead,
  headlineClassName,
  tail,
  align = 'left',
  tailFadeOnScroll = false,
}: ChapterStationProps) {
  const { tier } = useQuality();
  const ref = useRef<HTMLElement | null>(null);
  const tailFadeRef = useRef<HTMLDivElement | null>(null);
  // once: false → the reveal plays on entry AND reverses on exit, so text
  // never lingers from a station the user has snapped away from. amount:
  // 0.55 means the trigger flips when 55% of the station is in viewport,
  // which lines up with the hijack snap landing cleanly inside.
  const inView = useInView(ref, { amount: 0.55, once: false });

  // Scroll-driven tail fade. The motion variant handles entry/exit on
  // intersection; this RAF loop additionally multiplies the wrapper's
  // opacity by how-far-the-section-has-scrolled-past. Effective opacity
  // composites the two (browser applies them sequentially), so a tail
  // that's both not-yet-revealed AND scrolled past still ends up
  // invisible — no flash of CTA inheriting from one signal alone.
  useEffect(() => {
    if (!tailFadeOnScroll) return;
    if (tier !== 'high' && tier !== 'mid') return;

    let rafId = 0;
    // Cache the last value we wrote to the DOM. Two early-exits below
    // depend on this so we don't pay style-write cost on frames where
    // nothing visible has changed (typical case: user is far above or
    // far below this station — section out of viewport or scrolled
    // fully past).
    let lastWritten = -1;
    function tick() {
      const section = ref.current;
      const wrapper = tailFadeRef.current;
      if (!section || !wrapper) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;

      // Far-above gate: section is completely below the viewport (the
      // user hasn't reached it yet). Nothing to do — bail before the
      // arithmetic.
      if (rect.top > vh) {
        if (lastWritten !== 1) {
          wrapper.style.opacity = '1';
          lastWritten = 1;
        }
        rafId = requestAnimationFrame(tick);
        return;
      }

      const scrolledPast = Math.max(0, -rect.top);
      const fadeOut = Math.max(
        0,
        Math.min(1, scrolledPast / ((TAIL_SCROLL_FADE_VH * vh) / 100)),
      );
      const next = 1 - fadeOut;
      // Skip the style write when the computed opacity matches what's
      // already on the element — common at rest (fully visible or fully
      // faded) and saves a layout-flush trigger on every tick.
      if (Math.abs(next - lastWritten) > 0.001) {
        wrapper.style.opacity = String(next);
        lastWritten = next;
      }
      rafId = requestAnimationFrame(tick);
    }
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [tailFadeOnScroll, tier]);

  // Mobile renders MobileInfoSection instead — none of the clip-path /
  // blur / absolute-positioned column on this component fits a phone.
  // Gate AFTER hooks so render order stays stable across tier resolution
  // (Rules of Hooks: same hook call count every render).
  if (tier === 'low') return null;

  return (
    <section
      ref={ref}
      className={`chapter-station chapter-station--${chapter}`}
      data-chapter={chapter}
      // The headline IS the section's content label — using it directly
      // gives landmark-navigating screen-reader users a meaningful name
      // when listing the page's sections, instead of an anonymous
      // "section" entry.
      aria-label={headline}
    >
      <motion.div
        className="chapter-station__content pointer-events-none"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
      >
        <motion.h2
          className={`info-headline ${headlineClassName ?? ''}`}
          variants={headlineWipeVariants[align]}
        >
          {headline}
        </motion.h2>

        {lead && (
          <motion.p className="info-lead" variants={leadVariants}>
            {lead}
          </motion.p>
        )}

        {tail && (
          <motion.div className="chapter-station__tail" variants={tailVariants}>
            {/* Wrapper ref is the target for the scroll-driven opacity.
                Default `style.opacity = 1` so non-fade-on-scroll stations
                still render normally. */}
            <div ref={tailFadeRef} style={{ opacity: 1 }}>
              {tail}
            </div>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
